<template>
  <div v-if="tableInfo" class="row">
    <div class="col-sm-8" style="padding-left: 0px">
      <div class="card">
        <div class="card-block">
          <Editable
            v-if="!descriptionUpdating"
            :description="
              tableInfo.description === '\n\n' || tableInfo.description === '\n'
                ? ''
                : tableInfo.description
            "
            :descriptionSource="tableInfo.description_source"
            :entityId="tableKey"
            :entityType="datasetType"
            :disabled="!isUserSteward"
            :isStable="this.customFields.table_status === 'stable'"
            @update:description="tableDescriptionChanged"
          />
          <div v-if="descriptionUpdating" align="left">
            <p style="color: #00738e; font-size: 1.5em; margin-bottom: 0px">
              Description
            </p>
            <hr style="margin-top: 5px; width: 100%" />
            <Spinner :width="25" />
          </div>
        </div>
        <error-popup
          v-if="!descriptionUpdating && errorMessage"
          errorType="alert-danger"
          :message="errorMessage"
          class="card-block"
          style="padding: 0px 0px 10px 16px"
        />
      </div>
      <br />
      <div class="card">
        <div class="card-block">
          <PromiseOfFreshness :keyValue="tableKey" />
        </div>
      </div>
    </div>
    <div class="col-sm-4">
      <div class="card">
        <div class="card-block" style="display: flex; flex-direction: column; gap: 15px;">
          <DataProductLink :isDataPortalCurated="isDataPortalCurated" :tableKeyFormatted="tableKeyInResourceFormat" :isUserSteward="isUserSteward" />
          <Domain objectType="table" :objectKey="tableKey" :domainId="domainId" :disabled="!isUserSteward" :cluster="tableInfo.cluster" />
          <Status :keyValue="tableKey" />
          <OverviewFields
            heading="Data Owner"
            :keyValue="tableKey"
            :isUserSteward="isUserSteward"
            :isStable="this.customFields.table_status === 'stable'"
            :overviewFieldSource="squadEmailSource"
            entity="squad_email"
            objectType="table"
            tooltip="The contact information (team email) to get in touch with the data owner. Only stewards are allowed to edit."
          />
          <div v-if="!isDataOwnerPresent">
            <PotentialDataOwner :tableInfo="tableInfo" />
          </div>
          <OverviewFields
            heading="Slack Channel"
            :keyValue="tableKey"
            :isUserSteward="isUserSteward"
            :isStable="this.customFields.table_status === 'stable'"
            :overviewFieldSource="slackChannelSource"
            entity="slack_channel"
            objectType="table"
            :objectCluster="tableInfo.cluster"
            tooltip="The monitored support channel in Slack to get in touch with the data owner. Only stewards are allowed to edit."
          />
          <Stewards
            :keyValue="tableKey"
            objectType="table"
            :isStable="this.customFields.table_status === 'stable'"
            :isDataPortalCurated="isDataPortalCurated"
          />
          <hr class="custom-hr" />
          <OwnershipRole :ownershipRole="tableInfo.ownership_role" :cluster="tableInfo.cluster" :objectType="datasetType" />
          <div>
            <h5 class="inline">
              Frequent Users
            </h5>
            <Tooltip
              direction="top"
              contents="Top 5 Frequent Users of this table in the last 30 days"
              class="inline"
            >
              <IconInformationCircle size="md" weight="fill" class="text-primary" />
            </Tooltip>
            <frequent-users />
          </div>
          <Tags :keyValue="tableKey" :objectType="'table'" />
          <OverviewFields
            heading="Git Repository URL"
            :keyValue="tableKey"
            :isUserSteward="isUserSteward"
            :overviewFieldSource="gitRepositoryUrlSource"
            entity="git_repository_url"
            objectType="table"
            :isSingleValue="true"
            tooltip="The repository URL for this data object."
          />
          <OverviewFields
            heading="Transformation Tool"
            :keyValue="tableKey"
            :isUserSteward="isUserSteward"
            :overviewFieldSource="transformationToolSource"
            entity="transformation_tool"
            objectType="table"
            :isSingleValue="true"
            :suggestedOptions="['DBT','Python Notebook','Other']"
            tooltip="The tool used to transform this dataset."
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'vuex';
import { Tooltip } from '@cimpress/react-components';
import { IconInformationCircle } from '@cimpress-technology/react-streamline-icons/lib';
import Stewards from '../shared/overview/Stewards';
import OverviewFields from '../shared/overview/OverviewFields';
import Editable from '../shared/overview/Editable';
import OwnershipRole from '../shared/overview/OwnershipRole';
import Tags from '../shared/overview/Tags';
import FrequentUsers from './Overview/FrequentUsers';
import PromiseOfFreshness from './Overview/PromiseOfFreshness';
import ErrorPopup from '../ErrorPopup.vue';
import Domain from '../shared/overview/Domain.vue';
import PotentialDataOwner from '../shared/overview/PotentialDataOwner';
import DataProductLink from '../shared/overview/DataProductLink.vue';
import Status from '../shared/overview/Status.vue';
import Spinner from '../Icons/Spinner.vue';

export default {
  name: 'OverviewTab',
  inject: ['userManager', 'metadataPlatformClient'],
  components: {
    Editable,
    IconInformationCircle,
    Tooltip,
    OwnershipRole,
    Tags,
    Stewards,
    FrequentUsers,
    OverviewFields,
    PromiseOfFreshness,
    ErrorPopup,
    Domain,
    PotentialDataOwner,
    DataProductLink,
    Status,
    Spinner
  },
  data() {
    return {
      Active: 'overview',
      hover: false,
      showTooltip: false,
      isAddSteward: false,
      descriptionUpdating: false,
      errorMessage: null,
      datasetType: null

    };
  },
  computed: {
    ...mapState({
      tableInfo: state => state.tables.tableInfo,
      customFields: state => state.tables.customFields,
      squadEmailSource: state => (state.tables.tableInfo.programmatic_descriptions.find(p => p.source === 'squad_email_source') || {}).text,
      slackChannelSource: state => (state.tables.tableInfo.programmatic_descriptions.find(p => p.source === 'slack_channel_source') || {}).text,
      gitRepositoryUrlSource: state => (state.tables.tableInfo.programmatic_descriptions.find(p => p.source === 'git_repository_url_source') || {}).text,
      transformationToolSource: state => (state.tables.tableInfo.programmatic_descriptions.find(p => p.source === 'transformation_tool_source') || {}).text
    }),
    tableKey() {
      const { cluster, database, schema, name } = this.tableInfo || {};
      return `${database}://${cluster}.${schema}/${name}`;
    },
    tableKeyInResourceFormat() {
      const { cluster, database, schema, name } = this.tableInfo || {};
      const account = cluster.split('.')[0];
      return `${account}.${database}.${schema}.${name}`.toUpperCase();
    },
    tableKeyDecoded() {
      return encodeURIComponent(this.tableKey);
    },
    isUserSteward() {
      const userEmail = this.userManager.userData.app_metadata.canonical_id.toLowerCase();
      return !!this.tableInfo.owners.find(
        userData => userData.email.toLowerCase() === userEmail
      );
    },
    domainId() {
      const domainPD = (this.tableInfo.programmatic_descriptions || [])
        .find(b => b.source === 'domain_id') || {};
      return domainPD.text;
    },
    isDataOwnerPresent() {
      const pd = (this.tableInfo.programmatic_descriptions || [])
        .find(b => b.source === 'squad_email') || {};
      return pd.text && pd.text.length > 2;
    },
    isDataPortalCurated() {
      const getPdText = source => ((this.tableInfo.programmatic_descriptions || [])
        .find(b => b.source === source) || {}).text;
      const squad_email_source = getPdText('squad_email_source');
      const domain_id_source = getPdText('domain_id_source');
      const slack_channel_source = getPdText('slack_channel_source');
      const table_status_source = getPdText('table_status_source');
      const description_source = this.tableInfo.description_source;
      const owners_source = (this.tableInfo.owners || []).map(o => o.owner_source);
      return squad_email_source === 'data_portal_api'
        || domain_id_source === 'data_portal_api'
        || slack_channel_source === 'data_portal_api'
        || table_status_source === 'data_portal_api'
        || description_source === 'data_portal_api'
        || owners_source.includes('data_portal_api');
    }
  },
  created() {
    if (this.tableInfo) {
      this.datasetType = this.tableInfo.is_view ? 'View' : 'Table';
    }
  },
  methods: {
    ...mapActions({
      putTableDescription: 'tables/putTableDescription'
    }),
    async tableDescriptionChanged(event) {
      this.descriptionUpdating = true;
      this.errorMessage = null;
      try {
        await this.putTableDescription({
          platformClient: this.metadataPlatformClient,
          keyValue: this.tableKeyDecoded,
          description: event.description === '' ? '\n\n' : event.description
        });
        this.$ga.event('table', 'edit', unescape(this.tableKeyDecoded), 1);
      } catch (e) {
        this.errorMessage
          = e.message
          || e.data.message
          || e.data.title
          || (e.status && `Error occurred with status code ${e.status}`)
          || 'Unknown error occurred.';
        console.log(e);
      }
      this.descriptionUpdating = false;
    }
  }
};
</script>
<style scoped>
.custom-hr {
  border: 0;
  height: 1px;
  background: #c4cdd6;
  margin: 1px -16px;
}
</style>
